/**
 * The HTTP seam. One `fetch`-shaped function, so the session manager can be tested — and the
 * dev entry point can run without a deployed API — without either one stubbing globals.
 *
 * **Nothing about authorization lives here.** This module attaches no header, retries
 * nothing, and inspects no status code; it moves bytes. The binding header, the refresh
 * single-flight, and the teardown on a terminal `401` are `session.ts`'s, in one place,
 * because a second caller that "just adds the header itself" is how a route ends up outside
 * the session model on the client the same way `contracts.md` §5 describes on the server.
 */

import { API_BASE_URL } from './config'
import { REQUEST_TIMEOUT_MS, TIMED_OUT, deadline } from './timeout'

export interface ApiRequest {
  /** `PUT` added for P3's `PUT /gates/{id}/ble-credentials` — absent until P7 needed it. */
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Path only, e.g. `/auth/otp/start`. Never carries a token, binding, or phone number. */
  readonly path: string
  readonly headers?: Readonly<Record<string, string>>
  readonly body?: unknown
}

export interface ApiResponse {
  readonly status: number
  readonly body: unknown
}

export type ApiTransport = (request: ApiRequest) => Promise<ApiResponse>

/** Reachability failure — DNS, offline, CORS, abort. Distinct from a status-code refusal. */
export class NetworkError extends Error {
  constructor(cause?: unknown, message = 'network-unavailable') {
    super(message)
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/**
 * The server was **reached and could not serve the session** — a 5xx or an unusable body on
 * `POST /auth/refresh`. It is not the phone losing reception, and telling the user to check
 * their signal for it is a lie that costs them the only action they have.
 *
 * **A subclass, deliberately.** Seven call sites catch `NetworkError` to mean *"degrade to
 * offline-read-only"* — which is the right behavior here too, since neither condition is the
 * server refusing the session. Widening the hierarchy rather than adding a sibling leaves
 * every one of them behaving exactly as before; only a caller that tests the narrower type
 * sees a difference, and today that is the auth flow's copy alone.
 */
export class SessionUnavailableError extends NetworkError {
  constructor(cause?: unknown) {
    super(cause, 'session-unavailable')
    this.name = 'SessionUnavailableError'
  }
}

/**
 * **The deadline fired: the request was *sent* and abandoned, not never sent.** D189(b).
 *
 * That distinction is the whole of what PEC-13 turns on. A `fetch` that rejects in tens of
 * milliseconds — no reception, DNS, CORS — never reached the network, so retrying the command
 * locally over BLE is a **first** attempt. A request that ran the full {@link REQUEST_TIMEOUT_MS}
 * budget reached the network and may have reached the gate; a local retry there is PEC-13's
 * forbidden second dispatch, and the client must ask rather than assume.
 *
 * **A subclass, on {@link SessionUnavailableError}'s own reasoning.** Every
 * `instanceof NetworkError` catch site in the client means *"degrade to offline-read-only"*,
 * which is still right for a timeout; widening the hierarchy leaves all of them byte-identical,
 * and only the one caller that tests the narrower type — the BLE fallback decision — sees it.
 */
export class RequestTimeoutError extends NetworkError {
  constructor(cause?: unknown, message = 'network-unavailable') {
    super(cause, message)
    this.name = 'RequestTimeoutError'
  }
}

/**
 * **R1-1 — the one request timeout in the client, and it is here.**
 *
 * `fetch` has no default timeout, so before this a request that was accepted and never
 * answered — a dead cellular link that still completes a TCP handshake, a load balancer that
 * holds a connection open — produced an `await` that no code path could ever leave. Every
 * caller downstream sees that as the app being slow; the resident sees a control that never
 * comes back.
 *
 * Two rules, and neither is negotiable:
 *
 *  - **It is on the transport, not on `SessionManager`.** One place, so a route added later
 *    inherits it. A per-call-site timeout would be the fourteen-unguarded-await problem in a
 *    new costume.
 *  - **A deadline is a `NetworkError`, never a `SessionUnavailableError` and never a `401`.**
 *    Only a `401` ends a session (D129), and re-entry costs an SMS — so mapping a bad
 *    cellular moment onto anything else turns it into a sign-out. Because the existing type is
 *    reused, all ten honest catch sites and every `instanceof NetworkError` test keep working
 *    with no edit at all.
 *
 * The body read shares the same budget: a response whose headers arrive and whose body never
 * does is the same hang one layer down, and `deadline()` is reusable for exactly that reason.
 */
export const fetchTransport: ApiTransport = async (request) => {
  const budget = deadline(REQUEST_TIMEOUT_MS)
  try {
    const response = await budget.settle(
      fetch(`${API_BASE_URL}${request.path}`, {
        method: request.method,
        headers: {
          ...(request.body === undefined ? {} : { 'content-type': 'application/json' }),
          ...request.headers,
        },
        ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
        // No cookies: the session is a bearer token plus a binding header, and a credentialed
        // cross-origin request would invite CSRF into a design that currently has none.
        credentials: 'omit',
        cache: 'no-store',
        // Cancels the request itself, rather than leaving it running behind a caller that has
        // given up on it.
        signal: budget.signal,
      }),
    )
    if (response === TIMED_OUT) throw new RequestTimeoutError(new Error('request-timeout'))

    const text = await budget.settle(response.text())
    if (text === TIMED_OUT) throw new RequestTimeoutError(new Error('response-body-timeout'))

    let body: unknown = undefined
    if (text.length > 0) {
      try {
        body = JSON.parse(text) as unknown
      } catch {
        body = text
      }
    }
    return { status: response.status, body }
  } catch (error) {
    // Re-wrapping our own error would bury the cause one level deeper for no gain; anything
    // else — a rejected `fetch`, an aborted body stream — is the reachability failure this
    // type has always meant.
    throw error instanceof NetworkError ? error : new NetworkError(error)
  } finally {
    budget.cancel()
  }
}

/** Field lookup that keeps `unknown` honest, used by every response reader in this module. */
export function field(body: unknown, name: string): unknown {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return undefined
  return (body as Record<string, unknown>)[name]
}

export function stringField(body: unknown, name: string): string | undefined {
  const value = field(body, name)
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export function numberField(body: unknown, name: string): number | undefined {
  const value = field(body, name)
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}
